import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import { sendEmail, buildContactVisitorEmail, buildContactOwnerEmail, OWNER_EMAIL } from '../services/emailService';

export default function CTASection({ onExpertClick }) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.2 });

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    inquiryType: 'General Inquiry',
    domain: '',
    message: ''
  });
  const [status, setStatus] = useState('idle'); // idle, submitting, success, error
  const [errMsg, setErrMsg] = useState('');

  const domains = [
    'AI & Machine Learning',
    'IoT Systems',
    'Cloud Computing',
    'Data Science & Analytics',
    'Cybersecurity',
    'Blockchain',
    'Other'
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrMsg('');

    try {
      // 1. Save to Firebase
      await addDoc(collection(db, 'contact_inquiries'), {
        ...formData,
        createdAt: new Date().toISOString()
      });

      // 2. Send emails
      // To Visitor
      await sendEmail({
        to: formData.email,
        subject: `Thank you for contacting Connect Students`,
        text: buildContactVisitorEmail(formData)
      });

      // To Admin
      await sendEmail({
        to: OWNER_EMAIL,
        subject: formData.inquiryType === 'Project Collaboration' ? '🔥 NEW COLLABORATION LEAD' : '📬 NEW GENERAL INQUIRY',
        text: buildContactOwnerEmail(formData)
      });

      setStatus('success');
      setFormData({ name: '', phone: '', email: '', inquiryType: 'General Inquiry', domain: '', message: '' });
    } catch (err) {
      console.error('Contact Form Error:', err);
      setStatus('error');
      setErrMsg(err.message || 'Failed to submit. Please try again or contact via WhatsApp.');
    }
  };

  return (
    <>
      <section
        id="contact"
        ref={ref}
        className="relative py-28 overflow-hidden bg-parchment"
        style={{ backgroundColor: '#FAFAF5' }}
      >
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-royal-navy/5 to-transparent pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-royal-gold/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Column: Text & CTA */}
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7 }}
            >
              <div className="inline-flex items-center gap-2 bg-white shadow-sm border border-royal-navy/10 text-royal-navy text-sm font-bold px-5 py-2.5 rounded-full mb-8">
                <span className="w-2 h-2 rounded-full bg-royal-navy animate-pulse" />
                Start Today — Get Results
              </div>

              <h2 className="font-playfair text-4xl md:text-5xl lg:text-6xl font-bold text-royal-navy mb-6 leading-tight">
                Your Academic Success{' '}
                <span style={{ color: '#A07830' }}>Starts Here</span>
              </h2>

              <p className="font-inter text-lg text-royal-navy/70 mb-10 leading-relaxed font-medium">
                Join 5,000+ students who elevated their academic performance with
                our AI-powered research and project excellence. Get in touch to discuss collaboration.
              </p>

              <div className="flex flex-wrap gap-4 mb-12">
                <motion.button
                  onClick={onExpertClick}
                  whileHover={{ scale: 1.05, boxShadow: '0 8px 30px rgba(11,28,78,0.2)' }}
                  whileTap={{ scale: 0.97 }}
                  className="font-inter font-bold text-base px-8 py-4 rounded-full text-white"
                  style={{ background: '#0B1C4E' }}
                >
                  Talk to AI Expert
                </motion.button>
                <motion.a
                  href="tel:+917676654809"
                  whileHover={{ scale: 1.04 }}
                  className="border-2 border-royal-navy/20 text-royal-navy font-inter font-bold px-8 py-4 rounded-full hover:bg-royal-navy/5 transition-all duration-300 text-base inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z"/>
                  </svg>
                  Call Now
                </motion.a>
              </div>

              <div className="flex flex-wrap gap-6">
                {['100% Plagiarism Free', 'On-Time Delivery', '24/7 Expert Support'].map((badge) => (
                  <div key={badge} className="flex items-center gap-2 text-royal-navy/70 text-sm font-semibold">
                    <div className="w-4 h-4 rounded-full bg-royal-navy/10 flex items-center justify-center">
                      <svg className="w-2.5 h-2.5 text-royal-navy" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                    </div>
                    {badge}
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Right Column: Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={inView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="bg-white rounded-3xl shadow-card border border-royal-navy/10 p-8"
            >
              <h3 className="font-playfair font-bold text-2xl text-royal-navy mb-2">Get in Touch</h3>
              <p className="font-inter text-sm text-royal-navy/60 mb-6">Fill out the form below and our team will get back to you shortly.</p>

              {status === 'success' ? (
                <div className="bg-green-50 border border-green-200 text-green-800 rounded-xl p-6 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <h4 className="font-bold mb-1">Message Sent!</h4>
                  <p className="text-sm opacity-80">We'll be in touch with you very soon.</p>
                  <button onClick={() => setStatus('idle')} className="mt-4 text-sm font-semibold text-green-700 underline">Send another message</button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-semibold text-royal-navy/70 mb-1">Full Name *</label>
                      <input required type="text" name="name" value={formData.name} onChange={handleInputChange} className="w-full bg-parchment-light border border-royal-navy/20 rounded-xl px-4 py-2.5 text-sm font-medium text-royal-navy focus:outline-none focus:border-royal-gold transition-colors" placeholder="John Doe" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-royal-navy/70 mb-1">Phone Number *</label>
                      <input required type="tel" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full bg-parchment-light border border-royal-navy/20 rounded-xl px-4 py-2.5 text-sm font-medium text-royal-navy focus:outline-none focus:border-royal-gold transition-colors" placeholder="+91 9000000000" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-royal-navy/70 mb-1">Email Address *</label>
                    <input required type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full bg-parchment-light border border-royal-navy/20 rounded-xl px-4 py-2.5 text-sm font-medium text-royal-navy focus:outline-none focus:border-royal-gold transition-colors" placeholder="john@example.com" />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-royal-navy/70 mb-1">Inquiry Type *</label>
                    <div className="relative">
                      <select required name="inquiryType" value={formData.inquiryType} onChange={handleInputChange} className="w-full bg-parchment-light border border-royal-navy/20 rounded-xl px-4 py-2.5 text-sm font-medium text-royal-navy focus:outline-none focus:border-royal-gold transition-colors appearance-none cursor-pointer">
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Project Collaboration">Project Collaboration</option>
                        <option value="Other">Other</option>
                      </select>
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg className="w-4 h-4 text-royal-navy/50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                      </div>
                    </div>
                  </div>

                  {formData.inquiryType === 'Project Collaboration' && (
                    <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
                      <label className="block text-xs font-semibold text-royal-navy/70 mb-1 mt-4">Project Domain *</label>
                      <div className="relative">
                        <select required name="domain" value={formData.domain} onChange={handleInputChange} className="w-full bg-parchment-light border border-royal-navy/20 rounded-xl px-4 py-2.5 text-sm font-medium text-royal-navy focus:outline-none focus:border-royal-gold transition-colors appearance-none cursor-pointer">
                          <option value="">Select Domain</option>
                          {domains.map(d => <option key={d} value={d}>{d}</option>)}
                        </select>
                        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-4 h-4 text-royal-navy/50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                        </div>
                      </div>
                    </motion.div>
                  )}

                  <div>
                    <label className="block text-xs font-semibold text-royal-navy/70 mb-1 mt-4">Message / Requirements</label>
                    <textarea name="message" value={formData.message} onChange={handleInputChange} rows={3} className="w-full bg-parchment-light border border-royal-navy/20 rounded-xl px-4 py-3 text-sm font-medium text-royal-navy focus:outline-none focus:border-royal-gold transition-colors resize-none" placeholder="Tell us about what you're looking for..." />
                  </div>

                  {status === 'error' && <p className="text-red-500 text-xs font-semibold">{errMsg}</p>}

                  <button
                    type="submit"
                    disabled={status === 'submitting'}
                    className="w-full bg-royal-navy hover:bg-royal-navy/90 text-white font-bold py-3.5 rounded-xl transition-colors mt-2 disabled:opacity-70 flex items-center justify-center gap-2"
                  >
                    {status === 'submitting' ? (
                      <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"/> Sending...</>
                    ) : 'Send Message'}
                  </button>
                </form>
              )}
            </motion.div>

          </div>
        </div>
      </section>
    </>
  );
}
